import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

function CompanyDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [contracts, setContracts] = useState([]);
  
  // Editable Company Profile States
  const [legalName, setLegalName] = useState('');
  const [employeeCount, setEmployeeCount] = useState(0);
  const [activeProjects, setActiveProjects] = useState(0);
  const [expenses, setExpenses] = useState(0);

  // Contract Form States
  const [contractTitle, setContractTitle] = useState('');
  const [status, setStatus] = useState('Confirmed');
  const [totalAmount, setTotalAmount] = useState('');
  const [collectedAmount, setCollectedAmount] = useState('');
  
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id || id === 'undefined') {
      navigate('/', { replace: true });
      return;
    }

    const fetchCompanyData = async () => {
      try {
        const response = await fetch(`${API}/companies/${id}`, {
          headers: getAuthHeaders()
        });
        if (response.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        if (response.status === 403) {
          throw new Error('You do not have access to this company.');
        }
        if (!response.ok) throw new Error('Failed to fetch company data');
        const data = await response.json();
        setCompany(data);
        setContracts(data.contracts || []);
        
        setLegalName(data.legal_name || '');
        setEmployeeCount(data.employee_count || 0);
        setActiveProjects(data.active_projects || 0);
        setExpenses(data.expenses || 0);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id, navigate]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/companies/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          legal_name: legalName,
          employee_count: employeeCount,
          active_projects: activeProjects,
          expenses: expenses
        })
      });

      if (response.status === 401 || response.status === 403) {
        navigate('/login', { replace: true });
        return;
      }
      if (!response.ok) throw new Error('Failed to update profile');
      alert('Company profile updated successfully!');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveContract = async (e) => {
    e.preventDefault();
    const payload = {
      title: contractTitle,
      status: status,
      total_amount: parseFloat(totalAmount),
      collected_amount: parseFloat(collectedAmount)
    };

    try {
      const response = await fetch(`${API}/companies/${id}/contracts`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });

      if (response.status === 401 || response.status === 403) {
        navigate('/login', { replace: true });
        return;
      }
      if (!response.ok) throw new Error('Failed to create contract');

      const data = await response.json();
      setContracts([...contracts, data.contract]);
      setContractTitle('');
      setTotalAmount('');
      setCollectedAmount('');
      alert('Contract created successfully!');
    } catch (err) {
      alert(err.message);
    }
  };

  if (initialLoading) return <div style={{ padding: '40px' }}>Loading workspace...</div>;
  if (error) return <div style={{ padding: '40px', color: 'red' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      <div style={{ flex: 1, padding: '30px' }}>
        
        {/* TOP BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1>{company ? company.name : 'Workspace'}</h1>
          <button 
            onClick={() => navigate(`/dashboard/${id}/overview`)} 
            style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
          >
            ← Back to Company
          </button>
        </div>

        {/* MIDDLE SECTION: EDITABLE PROFILE & CONTRACT FORM */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
          
          {/* Editable Company Profile Card */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3>Company Profile</h3>
            <form onSubmit={handleUpdateProfile} style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px', fontWeight: '500' }}>Legal Name</label>
                <input 
                  type="text" 
                  value={legalName} 
                  onChange={(e) => setLegalName(e.target.value)} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px', fontWeight: '500' }}>Employee Count</label>
                <input 
                  type="number" 
                  value={employeeCount} 
                  onChange={(e) => setEmployeeCount(e.target.value)} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px', fontWeight: '500' }}>Active Projects</label>
                <input 
                  type="number" 
                  value={activeProjects} 
                  onChange={(e) => setActiveProjects(e.target.value)} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px', fontWeight: '500' }}>Base Expenses ($)</label>
                <input 
                  type="number" 
                  value={expenses} 
                  onChange={(e) => setExpenses(e.target.value)} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
              </div>

              <button 
                type="submit" 
                style={{ marginTop: '5px', padding: '10px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
              >
                Save Profile Changes
              </button>
            </form>
          </div>

          {/* Create New Contract Card */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3>Create New Contract</h3>
            <form onSubmit={handleSaveContract} style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Contract Title</label>
                <input 
                  type="text" 
                  value={contractTitle} 
                  onChange={(e) => setContractTitle(e.target.value)} 
                  required 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Status</label>
                <select 
                  value={status} 
                  onChange={(e) => setStatus(e.target.value)} 
                  style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: '#fff' }}
                >
                  <option value="Confirmed">Confirmed</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Total Amount ($)</label>
                  <input 
                    type="number" 
                    value={totalAmount} 
                    onChange={(e) => setTotalAmount(e.target.value)} 
                    required 
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Collected Amount ($)</label>
                  <input 
                    type="number" 
                    value={collectedAmount} 
                    onChange={(e) => setCollectedAmount(e.target.value)} 
                    required 
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <button 
                type="submit" 
                style={{ marginTop: '10px', padding: '10px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
              >
                Save Contract
              </button>
            </form>
          </div>

        </div>

        {/* CONTRACT HISTORY */}
        <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3>Contract History</h3>
          {contracts.length === 0 ? (
            <p style={{ color: '#64748b', marginTop: '15px' }}>No contracts found for this company.</p>
          ) : (
            <ul style={{ marginTop: '15px', listStyle: 'none', padding: 0 }}>
              {contracts.map((c, index) => (
                <li key={index} style={{ padding: '10px 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <span><strong>{c.title}</strong> ({c.status})</span>
                  <span>Total: ${c.total_amount} | Collected: ${c.collected_amount}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

      </div>
    </div>
  );
}

export default CompanyDashboard;